import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { money, fdate } from "@/lib/format";
import { PageHeader, EmptyState, StatusBadge } from "@/components/common";
import { TextField, AreaField, SelectField } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const today = new Date().toISOString().slice(0, 10);
const EMPTY = {
  owner_id: "", property_id: "", tenant_id: "", fecha_inicio: today, fecha_fin: "",
  renta_mensual: "", dia_pago: 1, deposito: "", incremento_anual: 0, interes_moratorio: 0,
  dias_tolerancia: 0, comision_tipo: "porcentaje", comision_valor: 10, condiciones: "", servicios_incluidos: "", estado: "vigente",
};

export default function Contracts() {
  const [items, setItems] = useState([]);
  const [owners, setOwners] = useState([]);
  const [props, setProps] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    api.get("/contracts").then((r) => setItems(r.data));
    api.get("/owners").then((r) => setOwners(r.data));
    api.get("/properties").then((r) => setProps(r.data));
    api.get("/tenants").then((r) => setTenants(r.data));
  };
  useEffect(() => { load(); }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const pickOwner = (oid) => setForm((f) => ({ ...f, owner_id: oid, property_id: "" }));
  const pickProperty = (pid) => {
    const p = props.find((x) => x.id === pid);
    setForm((f) => ({
      ...f,
      property_id: pid,
      renta_mensual: p?.renta_solicitada || f.renta_mensual,
      deposito: p?.deposito_solicitado || f.deposito,
      comision_tipo: p?.comision_propietario_tipo || f.comision_tipo,
      comision_valor: (p?.comision_propietario_valor ?? "") !== "" && p?.comision_propietario_valor ? p.comision_propietario_valor : f.comision_valor,
    }));
  };
  const availableProps = props.filter((p) => p.owner_id === form.owner_id && ["disponible", "en_proceso"].includes(p.estado));

  const save = async () => {
    if (!form.owner_id || !form.property_id || !form.tenant_id) return toast.error("Selecciona propietario, inmueble y arrendatario");
    if (!form.fecha_fin) return toast.error("Indica la fecha de terminación");
    if (!form.renta_mensual) return toast.error("Indica la renta mensual");
    try {
      const payload = {
        owner_id: form.owner_id, property_id: form.property_id, tenant_id: form.tenant_id,
        fecha_inicio: form.fecha_inicio, fecha_fin: form.fecha_fin,
        renta_mensual: parseFloat(form.renta_mensual), dia_pago: parseInt(form.dia_pago) || 1,
        deposito: parseFloat(form.deposito) || 0, incremento_anual: parseFloat(form.incremento_anual) || 0,
        interes_moratorio: parseFloat(form.interes_moratorio) || 0, dias_tolerancia: parseInt(form.dias_tolerancia) || 0,
        comision: { tipo: form.comision_tipo, valor: parseFloat(form.comision_valor) || 0 },
        condiciones: form.condiciones, servicios_incluidos: form.servicios_incluidos, estado: form.estado,
      };
      await api.post("/contracts", payload);
      toast.success("Contrato creado. Se generó la cobranza del mes.");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { if (!window.confirm("¿Eliminar contrato?")) return; await api.delete(`/contracts/${id}`); toast.success("Eliminado"); load(); };

  return (
    <div data-testid="contracts-page">
      <PageHeader title="Contratos" subtitle="Propietario → Inmueble → Arrendatario" actions={<Button data-testid="new-contract-btn" onClick={() => { setForm(EMPTY); setOpen(true); }} className="rounded-sm gap-2"><Plus size={16} /> Nuevo contrato</Button>} />
      {items.length === 0 ? (
        <EmptyState title="Aún no hay contratos" hint="Crea un contrato para generar la cobranza automáticamente." />
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Inmueble</TableHead><TableHead>Arrendatario</TableHead><TableHead>Propietario</TableHead><TableHead className="text-right">Renta</TableHead><TableHead>Vigencia</TableHead><TableHead>Comisión</TableHead><TableHead>Estado</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} data-testid={`contract-row-${c.id}`}>
                  <TableCell className="font-medium">{c.property_nombre}</TableCell>
                  <TableCell>{c.tenant_nombre}</TableCell>
                  <TableCell>{c.owner_nombre}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(c.renta_mensual)}</TableCell>
                  <TableCell className="text-xs">{fdate(c.fecha_inicio)} → {fdate(c.fecha_fin)}</TableCell>
                  <TableCell className="tabular-nums">{c.comision?.tipo === "fijo" ? money(c.comision?.valor) : `${c.comision?.valor}%`}</TableCell>
                  <TableCell><StatusBadge estado={c.estado} /></TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="icon" className="rounded-sm text-red-600" data-testid={`del-contract-${c.id}`} onClick={() => del(c.id)}><Trash size={16} /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuevo contrato</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SelectField label="Propietario" value={form.owner_id} onChange={pickOwner} testId="contract-owner" options={owners.map((o) => ({ value: o.id, label: o.nombre }))} />
            <SelectField label="Inmueble (solo disponibles)" value={form.property_id} onChange={pickProperty} testId="contract-property" options={availableProps.map((p) => ({ value: p.id, label: p.nombre }))} placeholder={form.owner_id ? "Selecciona inmueble…" : "Elige propietario primero"} />
            <SelectField label="Arrendatario" value={form.tenant_id} onChange={set("tenant_id")} testId="contract-tenant" options={tenants.map((t) => ({ value: t.id, label: t.nombre }))} col={2} />
            <TextField label="Fecha inicio" type="date" value={form.fecha_inicio} onChange={set("fecha_inicio")} testId="contract-inicio" />
            <TextField label="Fecha terminación" type="date" value={form.fecha_fin} onChange={set("fecha_fin")} testId="contract-fin" />
            <TextField label="Renta mensual" type="number" value={form.renta_mensual} onChange={set("renta_mensual")} testId="contract-renta" />
            <TextField label="Día de pago" type="number" value={form.dia_pago} onChange={set("dia_pago")} />
            <TextField label="Depósito" type="number" value={form.deposito} onChange={set("deposito")} />
            <TextField label="Incremento anual (%)" type="number" value={form.incremento_anual} onChange={set("incremento_anual")} />
            <TextField label="Interés moratorio (%)" type="number" value={form.interes_moratorio} onChange={set("interes_moratorio")} />
            <TextField label="Días de tolerancia" type="number" value={form.dias_tolerancia} onChange={set("dias_tolerancia")} />
            <SelectField label="Tipo de comisión" value={form.comision_tipo} onChange={set("comision_tipo")} testId="contract-comision-tipo" options={[{ value: "porcentaje", label: "Porcentaje de la renta" }, { value: "fijo", label: "Monto fijo" }]} />
            <TextField label={form.comision_tipo === "fijo" ? "Comisión ($)" : "Comisión (%)"} type="number" value={form.comision_valor} onChange={set("comision_valor")} testId="contract-comision-valor" />
            <AreaField label="Servicios incluidos" value={form.servicios_incluidos} onChange={set("servicios_incluidos")} />
            <AreaField label="Condiciones especiales" value={form.condiciones} onChange={set("condiciones")} />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm" data-testid="save-contract-btn" onClick={save}>Crear contrato</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
